import React, { useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { array, object, string } from "yup";
import UpcomingInstallmentForm from "../Layouts/UpcomingInstallmentForm";
import { UpcomingInstallmentValues } from "src/models/UpcomingInstallment.model";
import moment from "moment";
import {
  useEditInstallmentMutation,
  useGetInstallmentsQuery,
} from "src/services/InstallmentServices";
import { useParams } from "react-router-dom";
import { showToast } from "src/utils/showToaster";
import ATMFullPageLoader from "src/components/UI/atoms/ATMFullPageLoader/ATMFullPageLoader";

type Props = {
  onClose: () => void;
  batchId: string;
};

const EditUpcomingInstallmentWrapper = ({ onClose, batchId }: Props) => {
  const formikRef = useRef<any>(null);

  const { studentId = "" } = useParams();

  const [updateInstallment] = useEditInstallmentMutation();

  const { data, isLoading, isFetching } = useGetInstallmentsQuery({
    limit: 10,
    searchValue: "",
    params: ["studentName" , "amount"],
    page: 0,
    filterBy: [
      {
        fieldName: "studentId",
        value: studentId,
      },
      {
        fieldName: "isPayed",
        value: false,
      },
      {
        fieldName: "batchId",
        value: batchId,
      },
    ],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: false,
  });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      const installments = data?.data?.map((el: any) => ({
        dueDate: el?.dueDate || null,
        amount: el?.amount || "",
      }));
      formikRef.current?.setFieldValue("installments", installments);
      formikRef.current?.setFieldValue(
        "totalDueAmount",
        installments?.reduce((sum: number, installment: any) => {
          return sum + installment?.amount;
        }, 0)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  // Form Initial Values
  const initialValues: UpcomingInstallmentValues = {
    installments: [
      {
        dueDate: "",
        amount: "",
      },
    ],
    remark: "",
    totalDueAmount: 0,
  };

  // Validation Schema
  const validationSchema = object().shape({
    installments: array().of(
      object({
        dueDate: string().required("Please enter due date"),
        amount: string().required("Please enter amount"),
      })
    ),
  });

  // Handle Submit
  const handleSubmit = (
    values: UpcomingInstallmentValues,
    { setSubmitting, resetForm }: FormikHelpers<UpcomingInstallmentValues>
  ) => { 

    const installmentDate = values?.installments?.map((installment) => ({
      dueDate: moment(installment?.dueDate).format("yyyy-MM-DD"),
      amount: installment?.amount,
      remark: values?.remark,
    }))

    const formattedValues = {
      installmentData: installmentDate?.sort((a:any, b:any) => new Date(a?.dueDate)?.getTime() - new Date(b?.dueDate)?.getTime()) || [],
      batchId,
      studentId,
    };

    updateInstallment(formattedValues).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
          onClose();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Dialog open maxWidth="md" fullWidth>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            {isLoading || isFetching ? (
              <ATMFullPageLoader />
            ) : (
              <UpcomingInstallmentForm
                formikProps={formikProps}
                onClose={onClose}
              />
            )}
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditUpcomingInstallmentWrapper;
